import { useState } from 'react';

export default function HomePage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    consent: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.consent) return;
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new URLSearchParams();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('company', formData.company);
      formDataToSend.append('message', formData.message);
      formDataToSend.append('consent', '同意します');

      const response = await fetch('https://readdy.ai/api/form/d6n3ri14a3uojp5fh06g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formDataToSend.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', company: '', message: '', consent: false });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">

      {/* Hero Section */}
      <section className="relative pt-16 pb-16 sm:pt-24 sm:pb-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1A2332] via-[#2A3F5F] to-[#3A5F8F]"></div>
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7">
              <h1 className="mb-6 sm:mb-8">
                <div className="text-4xl sm:text-6xl lg:text-7xl font-black text-white mb-3 leading-[1.25]">『脳内カオス』</div>
                <div className="text-4xl sm:text-6xl lg:text-7xl font-black text-white mb-4 sm:mb-6 leading-[1.25]">解体プロジェクト</div>
                <div className="text-xl sm:text-3xl lg:text-4xl font-semibold text-white/90 mb-3 leading-[1.8]">AI×プロの壁打ちで、あなたを</div>
                <div className="text-3xl sm:text-5xl lg:text-6xl font-black text-[#FF6B35] mb-3 leading-[1.5]">最強の仕組み化マスター</div>
                <div className="text-xl sm:text-3xl lg:text-4xl font-semibold text-white/90 leading-[1.8]">に鍛え上げる180日間</div>
              </h1>

              <p className="text-base sm:text-lg text-white/80 mb-8 sm:mb-10 leading-[1.9]">
                社長の思考速度に組織が追いつく、一生モノのビジネス筋肉を獲得する
              </p>

              <div className="flex flex-wrap gap-4">
                <a
                  href="#program"
                  className="px-8 sm:px-10 py-4 sm:py-5 bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white font-bold rounded-full hover:shadow-2xl hover:scale-105 transition-all flex items-center gap-2 cursor-pointer whitespace-nowrap text-base sm:text-lg"
                >
                  プログラム詳細を見る
                  <i className="ri-arrow-right-line text-xl"></i>
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 hidden lg:block">
              <div className="relative">
                <div className="rounded-3xl overflow-hidden shadow-2xl w-full h-[520px]">
                  <img
                    src="https://public.readdy.ai/ai/img_res/d43d1f9ef1647f36b4294b2c3334eae9.jpg"
                    alt="ビジネスコーチング"
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1A2332]/30 to-transparent"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden shadow-2xl">
            <div className="relative bg-[#F5F1E8] min-h-[400px] lg:min-h-[600px]">
              <img
                src="https://readdy.ai/api/search-image?query=Japanese%20businessman%20in%20his%2040s%20sitting%20alone%20late%20at%20night%20in%20a%20dimly%20lit%20Tokyo%20office%2C%20back%20view%2C%20staring%20at%20a%20glowing%20computer%20screen%20surrounded%20by%20documents%20and%20papers%2C%20exhausted%20posture%2C%20moody%20blue%20and%20dark%20atmosphere%2C%20representing%20overwork%20and%20burnout%20in%20Japanese%20corporate%20culture%2C%20cinematic%20photography&width=800&height=900&seq=problem_jp_v2_001&orientation=portrait"
                alt="深夜作業"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A2332]/80 to-transparent"></div>
              <div className="absolute bottom-8 sm:bottom-12 left-6 sm:left-12 right-6 sm:right-12">
                <div className="text-2xl sm:text-4xl font-bold text-white mb-3 leading-[1.6]">『俺がやった方が早い』の罠</div>
                <div className="text-base sm:text-lg text-white/90 leading-[1.8]">この思考パターンが、組織の成長を止めています</div>
              </div>
            </div>

            <div className="bg-white p-8 sm:p-16">
              <div className="text-sm font-semibold text-[#FF6B35] tracking-wider mb-4">YOUR CHALLENGE</div>
              <h2 className="mb-8 sm:mb-12">
                <div className="text-2xl sm:text-3xl font-light text-[#1A2332] mb-3 leading-[1.8]">あなたの能力不足ではなく、</div>
                <div className="text-3xl sm:text-5xl font-black text-[#1A2332] mb-3 leading-[1.5]">『言語化コスト』が</div>
                <div className="text-2xl sm:text-3xl font-light text-[#1A2332] leading-[1.8]">真の敵です。</div>
              </h2>

              <div className="space-y-5 sm:space-y-6">
                <div className="flex gap-4">
                  <div className="w-2 h-2 bg-[#FF6B35] rounded-sm mt-3 flex-shrink-0"></div>
                  <p className="text-base sm:text-lg text-gray-700 leading-[1.9]">
                    社長の思考速度に組織が追いつけないのは、翻訳作業に膨大なエネルギーが必要だから。
                  </p>
                </div>
                <div className="flex gap-4">
                  <div className="w-2 h-2 bg-[#FF6B35] rounded-sm mt-3 flex-shrink-0"></div>
                  <p className="text-base sm:text-lg text-gray-700 leading-[1.9]">
                    「俺がやった方が早い」の罠に陥り、組織の成長スピードが社長個人のリソースに依存しています。
                  </p>
                </div>
                <div className="flex gap-4">
                  <div className="w-2 h-2 bg-[#FF6B35] rounded-sm mt-3 flex-shrink-0"></div>
                  <p className="text-base sm:text-lg text-gray-700 leading-[1.9] font-bold">
                    深夜に一人でパソコンに向かい、業務フローや指示書を作る日々を、今日で終わりにしませんか？
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section id="program" className="py-16 sm:py-24 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col lg:flex-row justify-between items-start mb-12 sm:mb-16">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-[#1A2332] mb-6 lg:mb-0 leading-[1.5]">
              第3の選択肢
            </h2>
            <p className="text-base sm:text-lg text-gray-600 max-w-xl leading-[1.9]">
              「教える」でも「丸投げ」でもない、新しいアプローチ。<br />
              あなた自身のビジネス筋肉として完全に定着させます。
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="bg-[#F8F9FA] rounded-3xl overflow-hidden">
              <div className="h-56 sm:h-64 w-full">
                <img
                  src="https://readdy.ai/api/search-image?query=Japanese%20adult%20student%20watching%20an%20online%20video%20course%20alone%20on%20a%20laptop%20at%20home%2C%20cold%20blue%20screen%20light%20illuminating%20their%20face%2C%20isolated%20and%20impersonal%20learning%20environment%2C%20showing%20the%20limitations%20of%20self-paced%20digital%20education%20without%20a%20mentor%2C%20realistic%20Japanese%20home%20setting&width=500&height=400&seq=solution_jp_v2_001&orientation=landscape"
                  alt="一般的なスクール"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6 sm:p-8">
                <h3 className="text-lg sm:text-xl font-bold text-[#1A2332] mb-4 leading-[1.7]">❌ 教える（一般的なスクール）</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-[1.9]">
                  やり方の動画教材やマニュアルを渡されても、忙しい社長は結局やる時間を確保できません。知識だけでは実務は動きません。
                </p>
              </div>
            </div>

            <div className="bg-[#F8F9FA] rounded-3xl overflow-hidden">
              <div className="h-56 sm:h-64 w-full">
                <img
                  src="https://readdy.ai/api/search-image?query=Japanese%20office%20worker%20in%20a%20generic%20corporate%20Tokyo%20office%20creating%20standard%20business%20documents%20at%20a%20desk%2C%20representing%20outsourced%20impersonal%20work%2C%20showing%20the%20disconnect%20between%20a%20contractor%20and%20the%20client%20company%20vision%2C%20professional%20but%20soulless%20atmosphere&width=500&height=400&seq=solution_jp_v2_002&orientation=landscape"
                  alt="代行業者"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6 sm:p-8">
                <h3 className="text-lg sm:text-xl font-bold text-[#1A2332] mb-4 leading-[1.7]">❌ 丸投げ（代行業者）</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-[1.9]">
                  業者に依頼しても、社長の熱量や魂がこもらず、結果として「現場で使えない」表面的なアウトプットに終わります。
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-[#FF6B35] to-[#FF8C42] rounded-3xl overflow-hidden shadow-xl">
              <div className="h-56 sm:h-64 w-full">
                <img
                  src="https://readdy.ai/api/search-image?query=Japanese%20business%20coach%20in%20his%2040s%20and%20a%20Japanese%20entrepreneur%20in%20their%2030s%20doing%20an%20intensive%20one-on-one%20coaching%20session%2C%20both%20looking%20at%20a%20laptop%20screen%20together%2C%20collaborative%20and%20energetic%20atmosphere%20in%20a%20bright%20modern%20Tokyo%20office%2C%20showing%20personal%20skill%20transfer%20and%20direct%20guidance&width=500&height=400&seq=solution_jp_v2_003&orientation=landscape"
                  alt="パーソナル伴走"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="p-6 sm:p-8">
                <h3 className="text-lg sm:text-xl font-bold text-white mb-4 leading-[1.7]">✓ パーソナル伴走（本プログラム）</h3>
                <p className="text-sm sm:text-base text-white leading-[1.9]">
                  画面を共有しながら、プロの視点で「AIへの問い方」を直接指導。あなた自身のビジネス筋肉として完全に定着させます。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Target Audience Section */}
      <section className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-5xl font-black text-[#1A2332] mb-4 leading-[1.5]">代行ではなく「自立」を。</h2>
            <p className="text-lg sm:text-xl text-gray-600 leading-[1.8]">2つの限界突破ロードマップ</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
            {/* PLAN A */}
            <div className="relative rounded-3xl overflow-hidden min-h-[560px] sm:min-h-[640px] flex flex-col justify-end shadow-2xl">
              <div className="absolute inset-0">
                <img
                  src="https://readdy.ai/api/search-image?query=Japanese%20solo%20entrepreneur%20woman%20in%20her%2030s%20working%20confidently%20on%20a%20laptop%20in%20a%20bright%20minimalist%20modern%20Tokyo%20workspace%2C%20natural%20daylight%20streaming%20through%20large%20windows%2C%20clean%20white%20desk%20with%20plants%2C%20empowering%20and%20productive%20atmosphere%2C%20high%20quality%20portrait%20photography&width=700&height=900&seq=planA_jp_v3_001&orientation=portrait"
                  alt="一人社長・個人事業主"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A2332]/90 via-[#1A2332]/40 to-transparent"></div>
              </div>
              <div className="relative p-8 sm:p-12">
                <div className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full mb-5">
                  <span className="text-white text-xs font-bold tracking-widest">PLAN A</span>
                </div>
                <h3 className="mb-4 sm:mb-6">
                  <div className="text-2xl sm:text-3xl font-light text-white mb-2 leading-[1.7]">一人社長・</div>
                  <div className="text-3xl sm:text-4xl font-black text-white mb-3 leading-[1.5]">個人事業主の方へ</div>
                  <div className="text-xl sm:text-2xl font-semibold text-[#FF8C42] leading-[1.6]">10人分の仕事量を1人で回す</div>
                </h3>
                <p className="text-sm sm:text-base text-white/85 leading-[1.9]">
                  あなた自身がAIスキルを血肉にし、10人分の仕事量を1人で回す「限界突破」のパーソナル伴走。頭の中にある「やりたいこと」や思いついたアイデアを、その日のうちに即日形にする、最強の「あなた自身の分身」を作り上げます。
                </p>
              </div>
            </div>

            {/* PLAN B */}
            <div className="relative rounded-3xl overflow-hidden min-h-[560px] sm:min-h-[640px] flex flex-col justify-end shadow-2xl">
              <div className="absolute inset-0">
                <img
                  src="https://readdy.ai/api/search-image?query=Japanese%20CEO%20in%20his%2040s%20and%20a%20young%20Japanese%20employee%20in%20their%20late%2020s%20having%20an%20intensive%20one-on-one%20mentoring%20session%20in%20a%20modern%20Tokyo%20boardroom%2C%20both%20focused%20on%20a%20laptop%20screen%2C%20showing%20leadership%20development%20and%20skill%20transfer%2C%20professional%20and%20dynamic%20atmosphere%2C%20high%20quality%20photography&width=700&height=900&seq=planB_jp_v3_001&orientation=portrait"
                  alt="社員を抱える社長"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#FF6B35]/90 via-[#FF6B35]/30 to-transparent"></div>
              </div>
              <div className="relative p-8 sm:p-12">
                <div className="inline-block px-3 py-1 bg-white/20 backdrop-blur-sm rounded-full mb-5">
                  <span className="text-white text-xs font-bold tracking-widest">PLAN B</span>
                </div>
                <h3 className="mb-4 sm:mb-6">
                  <div className="text-2xl sm:text-3xl font-light text-white mb-2 leading-[1.7]">社員を抱える</div>
                  <div className="text-3xl sm:text-4xl font-black text-white mb-3 leading-[1.5]">社長へ</div>
                  <div className="text-xl sm:text-2xl font-semibold text-white/90 leading-[1.6]">最強の「右腕」を社内に育てる</div>
                </h3>
                <p className="text-sm sm:text-base text-white/85 leading-[1.9]">
                  社長の思考を形にできるエース社員を1名、私に預けてください。私が彼らを<span className="font-bold text-white">「社内AI-COO（最高執行責任者）」</span>として徹底的に鍛え上げ、社長の言語化コストをゼロにする、最強の「右腕」へと育成します。
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Program Details Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-3 h-3 bg-[#FF6B35] rotate-45"></div>
              <span className="text-sm font-semibold text-[#FF6B35] tracking-wider">PROGRAM DETAILS</span>
            </div>
            <h2 className="text-3xl sm:text-5xl font-black text-[#1A2332] leading-[1.5]">6ヶ月間で得られる成果</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 mb-6 sm:mb-8">
            <div className="bg-[#E8F4F8] rounded-3xl p-8 sm:p-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FF6B35] rounded-full">
                  <i className="ri-video-line text-white text-xl"></i>
                </div>
                <span className="text-sm font-semibold text-[#1A2332]">月2回 / 各90分</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#1A2332] mb-4 leading-[1.6]">実践型ライブ・トレーニング</h3>
              <p className="text-sm sm:text-base text-gray-700 leading-[1.9]">
                思考のボトルネックをその場で整理し、あなた（または社員）に実際にAIを操作させながら、最適なプロンプトの型と実務への落とし込み方を直接指導します。
              </p>
            </div>

            <div className="bg-[#E8F4F8] rounded-3xl p-8 sm:p-10">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-[#FF6B35] rounded-full">
                  <i className="ri-chat-3-line text-white text-xl"></i>
                </div>
                <span className="text-sm font-semibold text-[#1A2332]">1日1トピック・平日対応</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#1A2332] mb-4 leading-[1.6]">チャット壁打ちサポート</h3>
              <p className="text-sm sm:text-base text-gray-700 leading-[1.9]">
                セッションの合間も、日常の「これ、どう言語化してAIに投げればいい？」という迷いにプロが並走し、即座に解決に導きます。止まる時間を無くします。
              </p>
            </div>

            <div className="relative rounded-3xl overflow-hidden w-full h-full min-h-[320px] sm:min-h-[360px]">
              <img
                src="https://readdy.ai/api/search-image?query=Japanese%20business%20coach%20in%20his%2040s%20guiding%20a%20Japanese%20client%20through%20AI%20tools%20on%20a%20laptop%20screen%20during%20a%20real%20coaching%20session%2C%20collaborative%20Japanese%20workspace%2C%20professional%20training%20atmosphere%2C%20authentic%20learning%20moment%2C%20warm%20office%20lighting%2C%20high%20quality%20photography&width=500&height=600&seq=program_jp_v2_001&orientation=portrait"
                alt="実際のセッション風景"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A2332]/80 to-transparent"></div>
              <div className="absolute bottom-8 left-8 right-8">
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-2 leading-[1.6]">実際のセッション風景</h3>
                <p className="text-white/90 leading-[1.8]">画面共有で直接指導</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3つのお約束 */}
      <section className="py-16 sm:py-24 bg-[#1A2332]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="w-3 h-3 bg-[#FF6B35] rotate-45"></div>
              <span className="text-sm font-semibold text-[#FF6B35] tracking-wider">OUR COMMITMENT</span>
            </div>
            <h2 className="text-3xl sm:text-5xl font-black text-white leading-[1.5]">成果を出すための<br />「3つのお約束」</h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
            <div className="bg-white/5 border border-white/10 rounded-3xl p-8 sm:p-10 hover:bg-white/10 transition-all">
              <div className="w-14 h-14 flex items-center justify-center bg-[#FF6B35] rounded-2xl mb-6">
                <span className="text-white text-2xl font-black">1</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-black text-white mb-4 leading-[1.6]">1セッション・1テーマ</h3>
              <p className="text-sm sm:text-base text-white/70 leading-[1.9]">
                散漫な会議を禁止します。90分のセッションで1つのテーマに絞り込み、確実に「1つの成果」を出し切ることに集中します。
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-3xl p-8 sm:p-10 hover:bg-white/10 transition-all">
              <div className="w-14 h-14 flex items-center justify-center bg-[#FF6B35] rounded-2xl mb-6">
                <span className="text-white text-2xl font-black">2</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-black text-white mb-4 leading-[1.6]">8割完成主義</h3>
              <p className="text-sm sm:text-base text-white/70 leading-[1.9]">
                完璧さよりも圧倒的なスピードを重視します。持ち帰って検討するのではなく、その場で「動くもの（使える状態）」を作ります。
              </p>
            </div>

            <div className="bg-white/5 border border-white/10 rounded-3xl p-8 sm:p-10 hover:bg-white/10 transition-all">
              <div className="w-14 h-14 flex items-center justify-center bg-[#FF6B35] rounded-2xl mb-6">
                <span className="text-white text-2xl font-black">3</span>
              </div>
              <h3 className="text-xl sm:text-2xl font-black text-white mb-4 leading-[1.6]">丸投げの禁止</h3>
              <p className="text-sm sm:text-base text-white/70 leading-[1.9]">
                私は代行業者ではなく、あなたの「トレーナー」です。一生モノのスキルにするため、セッション外での作業丸投げは一切お断りし、ご自身で手を動かしていただきます。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 1: 180日間の投資額 */}
      <section id="pricing" className="py-16 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1A2332] via-[#2A3F5F] to-[#3A5F8F]"></div>
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full mb-6">
                <span className="text-white text-sm font-medium">INVESTMENT</span>
              </div>

              <h2 className="mb-6 sm:mb-8">
                <div className="text-2xl sm:text-4xl font-light text-white mb-3 leading-[1.8]">180日間の</div>
                <div className="text-5xl sm:text-7xl font-black text-white mb-4 sm:mb-5 leading-[1.3]">投資額</div>
                <div className="text-lg sm:text-2xl font-light text-white/90 leading-[1.8]">一生モノのビジネス筋肉を獲得する</div>
              </h2>

              <p className="text-base sm:text-lg text-white/80 mb-8 sm:mb-10 leading-[1.9]">
                月額換算で11万円。週に数日来るだけのアルバイトを雇うのと同等の投資です。<br />
                一時的な代行作業への支出ではなく、あなた（または右腕社員）の中に永遠に残る<br />
                「AIを駆使した仕組み化スキル」を手に入れるための自己投資です。
              </p>

              <a
                href="#contact"
                className="inline-flex items-center gap-3 px-8 sm:px-10 py-4 sm:py-5 bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white font-bold rounded-full hover:shadow-2xl hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
              >
                今すぐ申し込む
                <i className="ri-arrow-right-line text-xl"></i>
              </a>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-white rounded-[2rem] p-8 sm:p-12 shadow-2xl">
                <div className="text-center">
                  <div className="text-base text-gray-500 mb-2">正規価格（税込）</div>
                  <div className="text-4xl sm:text-5xl font-black text-[#1A2332] mb-2 leading-[1.4]">660,000円</div>
                  <div className="text-base text-gray-500 mb-8">（半年間の投資額・税込）</div>

                  <div className="mt-8 pt-8 border-t border-gray-200">
                    <div className="space-y-3 text-left">
                      <div className="flex items-center gap-3">
                        <i className="ri-check-line text-[#FF6B35] text-xl"></i>
                        <span className="text-sm sm:text-base text-gray-700">実践型ライブ・トレーニング（月2回 / 各90分）</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <i className="ri-check-line text-[#FF6B35] text-xl"></i>
                        <span className="text-sm sm:text-base text-gray-700">チャット壁打ちサポート（平日対応）</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <i className="ri-check-line text-[#FF6B35] text-xl"></i>
                        <span className="text-sm sm:text-base text-gray-700">6ヶ月間の完全伴走</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: コスト比較 */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="mb-12 sm:mb-16">
            <div className="text-sm font-semibold text-[#FF6B35] tracking-wider mb-4">COST COMPARISON</div>
            <h2 className="text-3xl sm:text-5xl lg:text-6xl font-black text-[#1A2332] mb-3 leading-[1.5]">
              優秀な『右腕』を一人雇う
            </h2>
            <h2 className="text-3xl sm:text-5xl lg:text-6xl font-black text-[#1A2332] leading-[1.5]">
              コストと比較してください
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
            <div className="rounded-3xl overflow-hidden shadow-lg">
              <div className="h-64 sm:h-80 w-full bg-[#FFD6D6]">
                <img
                  src="https://readdy.ai/api/search-image?query=Japanese%20full-time%20salaried%20employee%20in%20formal%20business%20attire%20working%20at%20a%20desk%20in%20a%20large%20Tokyo%20corporate%20office%2C%20representing%20traditional%20employment%20and%20high%20fixed%20costs%2C%20professional%20Japanese%20corporate%20environment%2C%20clean%20and%20formal%20atmosphere&width=400&height=500&seq=cost_jp_v2_001&orientation=portrait"
                  alt="正社員採用"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="bg-white p-5 sm:p-6">
                <h3 className="text-base sm:text-lg font-bold text-[#1A2332] mb-3 leading-[1.6]">正社員の採用・育成</h3>
                <div className="text-2xl sm:text-3xl font-black text-red-600 mb-1">約2,500,000円〜</div>
                <div className="text-sm text-gray-500">(半年換算)</div>
              </div>
            </div>

            <div className="rounded-3xl overflow-hidden shadow-lg">
              <div className="h-64 sm:h-80 w-full bg-[#D4F1D4]">
                <img
                  src="https://readdy.ai/api/search-image?query=Japanese%20freelance%20professional%20working%20remotely%20on%20a%20laptop%20at%20a%20home%20office%20in%20Japan%2C%20representing%20outsourced%20contractor%20arrangement%2C%20showing%20the%20external%20director%20model%2C%20clean%20Japanese%20home%20workspace%20with%20natural%20light&width=400&height=500&seq=cost_jp_v2_002&orientation=portrait"
                  alt="外注ディレクター"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="bg-white p-5 sm:p-6">
                <h3 className="text-base sm:text-lg font-bold text-[#1A2332] mb-3 leading-[1.6]">外注ディレクター契約</h3>
                <div className="text-2xl sm:text-3xl font-black text-green-600 mb-1">1,200,000円</div>
                <div className="text-sm text-gray-500">(半年換算)</div>
              </div>
            </div>

            <div className="rounded-3xl overflow-hidden shadow-lg">
              <div className="h-64 sm:h-80 w-full bg-[#E8E8E8]">
                <img
                  src="https://readdy.ai/api/search-image?query=Japanese%20businessman%20in%20his%2040s%20looking%20stressed%20and%20overwhelmed%20at%20his%20desk%2C%20surrounded%20by%20piles%20of%20documents%20and%20a%20clock%20on%20the%20wall%2C%20representing%20lost%20time%20and%20opportunity%20cost%2C%20dramatic%20moody%20lighting%2C%20showing%20the%20hidden%20cost%20of%20a%20busy%20Japanese%20entrepreneur&width=400&height=500&seq=cost_jp_v2_003&orientation=portrait"
                  alt="機会損失"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="bg-white p-5 sm:p-6">
                <h3 className="text-base sm:text-lg font-bold text-[#1A2332] mb-3 leading-[1.6]">社長自身の時給損失</h3>
                <div className="text-2xl sm:text-3xl font-black text-gray-600 mb-1">プライスレス</div>
                <div className="text-sm text-gray-500">(数百万〜)</div>
              </div>
            </div>

            <div className="rounded-3xl overflow-hidden shadow-lg relative">
              <div className="absolute top-4 right-4 z-10 bg-[#FF6B35] text-white px-3 py-1.5 rounded-full text-xs font-bold transform rotate-12 shadow-lg whitespace-nowrap">
                最もコスパが高い
              </div>
              <div className="h-64 sm:h-80 w-full bg-[#F4E4C1]">
                <img
                  src="https://readdy.ai/api/search-image?query=Japanese%20entrepreneur%20in%20their%2030s%20looking%20confident%20and%20empowered%20while%20using%20AI%20tools%20on%20a%20laptop%2C%20bright%20optimistic%20modern%20workspace%20in%20Japan%2C%20representing%20a%20smart%20and%20cost-effective%20investment%20in%20skills%2C%20warm%20and%20energetic%20atmosphere&width=400&height=500&seq=cost_jp_v2_004&orientation=portrait"
                  alt="本プログラム"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="bg-white p-5 sm:p-6">
                <h3 className="text-base sm:text-lg font-bold text-[#1A2332] mb-3 leading-[1.6]">本プログラム</h3>
                <div className="text-3xl sm:text-4xl font-black text-[#FF6B35] mb-1">660,000円</div>
                <div className="text-sm text-gray-500">(半年間の投資額)</div>
              </div>
            </div>
          </div>

          <div className="mt-10 sm:mt-12 bg-gradient-to-r from-[#F8F9FA] to-[#E8F4F8] rounded-3xl p-8 sm:p-10">
            <p className="text-base sm:text-lg text-gray-700 leading-[1.9] text-center">
              正社員採用の固定費や、終わりのない外注費、何より<span className="font-bold text-[#FF6B35]">「社長自身の貴重な時間」</span>の損失と比較してください。<br />
              本プログラムは、圧倒的なコストパフォーマンスで御社内に<span className="font-bold text-[#1A2332]">「自走する仕組み」</span>を構築します。
            </p>
          </div>
        </div>
      </section>

      {/* Section 3: 先着5名様限定 創業期モニター */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden shadow-2xl">
            <div className="relative min-h-[400px] sm:min-h-[560px]">
              <img
                src="https://readdy.ai/api/search-image?query=Abstract%20flat%20lay%20of%20a%20special%20limited%20offer%20concept%2C%20a%20golden%20ticket%20or%20exclusive%20badge%20on%20a%20dark%20navy%20background%2C%20surrounded%20by%20minimalist%20business%20icons%20like%20a%20calendar%2C%20a%20star%2C%20and%20a%20checkmark%2C%20representing%20a%20rare%20and%20valuable%20opportunity%2C%20clean%20modern%20graphic%20design%2C%20no%20people&width=800&height=900&seq=monitor_v3_001&orientation=portrait"
                alt="創業期モニター"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1A2332]/85 to-[#1A2332]/20"></div>
              <div className="absolute bottom-8 sm:bottom-12 left-6 sm:left-12 right-6 sm:right-12">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF6B35] rounded-full mb-4">
                  <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
                  <span className="text-white text-sm font-bold">先着5名様限定</span>
                </div>
                <h3 className="text-3xl sm:text-5xl font-black text-white mb-3 leading-[1.4]">創業期モニター<br />ご案内</h3>
                <p className="text-white/90 text-base sm:text-lg leading-[1.8]">共に「圧倒的な成果事例」を創り上げる<br />初期モニター企業様を限定募集</p>
              </div>
            </div>

            <div className="bg-white p-8 sm:p-16 flex flex-col justify-center">
              <div className="text-sm font-semibold text-[#FF6B35] tracking-wider mb-4">FOUNDING MONITOR</div>
              <h3 className="text-3xl sm:text-4xl font-black text-[#1A2332] mb-6 leading-[1.5]">
                特別価格でご提供
              </h3>

              <p className="text-base sm:text-lg text-gray-700 leading-[1.9] mb-8">
                本プログラムの「圧倒的な成果事例」を共に創り上げていただける、初期モニター企業様を限定募集いたします。<br /><br />
                プログラム終了後の<span className="font-bold text-[#1A2332]">事例インタビュー（顔出し・実名）</span>と、サービス向上に向けたフィードバックへのご協力を条件に、正規価格の半額にてご提供させていただきます。
              </p>

              <div className="bg-[#FFF5F0] rounded-2xl p-6 sm:p-8 mb-8">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-base text-gray-500">正規価格</span>
                  <span className="text-xl sm:text-2xl text-gray-400 line-through">660,000円</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-base font-bold text-[#FF6B35]">モニター特別価格</span>
                  <div className="text-right">
                    <div className="text-4xl sm:text-5xl font-black text-[#FF6B35] leading-[1.3]">330,000円</div>
                    <div className="text-sm text-gray-500">（税込）</div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-[#FFD6C0]">
                  <div className="flex items-center gap-2">
                    <i className="ri-alarm-warning-line text-[#FF6B35]"></i>
                    <span className="text-sm font-bold text-[#FF6B35]">残り枠：先着5名様のみ</span>
                  </div>
                </div>
              </div>

              <a
                href="#contact"
                className="inline-flex items-center justify-center gap-3 px-8 sm:px-10 py-4 sm:py-5 bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white font-bold rounded-full hover:shadow-2xl hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
              >
                モニター枠に申し込む
                <i className="ri-arrow-right-line text-xl"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: 3日以内の決断特典（コンパクト版） */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="bg-gradient-to-br from-[#FF6B35] to-[#FF8C42] rounded-3xl p-8 sm:p-12 shadow-2xl">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
              <div className="w-14 h-14 flex items-center justify-center bg-white rounded-2xl flex-shrink-0">
                <i className="ri-timer-flash-line text-[#FF6B35] text-2xl"></i>
              </div>
              <div>
                <div className="text-xs font-semibold text-white/70 tracking-wider mb-1">SPECIAL BONUS</div>
                <h3 className="text-2xl sm:text-3xl font-black text-white leading-[1.4]">3日以内の決断特典</h3>
              </div>
            </div>

            <p className="text-base sm:text-lg text-white/90 leading-[1.9] mb-6">
              迷っている時間は「言語化コスト」の無駄遣いです。ご提案から<strong>3日以内</strong>に決断いただいた方には、スタートダッシュを完璧にするための<strong>初回キックオフ・全体整理セッション（120分）</strong>を無料で追加いたします。
            </p>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div className="bg-white/20 backdrop-blur-sm rounded-2xl px-6 py-4">
                <div className="text-sm text-white/80 mb-1">通常価格換算</div>
                <div className="text-2xl font-black text-white line-through opacity-60">約10万円相当</div>
                <div className="text-4xl font-black text-white">→ 無料</div>
              </div>
              <a
                href="#contact"
                className="inline-flex items-center gap-3 px-7 py-4 bg-white text-[#FF6B35] font-bold rounded-full hover:shadow-xl hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
              >
                3日以内に決断して特典を獲得
                <i className="ri-arrow-right-line text-xl"></i>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-14 sm:py-20 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-3xl overflow-hidden shadow-2xl">
            <div className="relative bg-[#F5F1E8] min-h-[360px] sm:min-h-[500px]">
              <img
                src="https://readdy.ai/api/search-image?query=A%20vibrant%20and%20energetic%20Japanese%20office%20team%20of%20four%20people%20in%20their%2040s%20and%2050s%2C%20laughing%20and%20collaborating%20around%20a%20large%20meeting%20table%20with%20laptops%20and%20whiteboards%2C%20dynamic%20and%20lively%20atmosphere%2C%20modern%20bright%20Tokyo%20office%20with%20large%20windows%2C%20warm%20natural%20light%2C%20showing%20active%20teamwork%20and%20business%20success%2C%20high%20quality%20photography&width=800&height=900&seq=final_jp_v4_team_001&orientation=portrait"
                alt="活気ある社内チーム"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-white/70 to-transparent"></div>
              <div className="absolute bottom-6 sm:bottom-10 left-6 sm:left-12 right-6 sm:right-12">
                <h3 className="text-2xl sm:text-4xl font-black text-[#1A2332] leading-[1.5]">一生モノの<br />『仕組み化スキル』を。</h3>
              </div>
            </div>

            <div className="bg-white p-8 sm:p-14 flex flex-col justify-center">
              <p className="text-base sm:text-xl text-gray-700 leading-[1.9] mb-5">
                一人で抱え込み、現場の火消しに追われる日々は、今日で終わりにしましょう。
              </p>
              <p className="text-base sm:text-xl text-gray-700 leading-[1.9] mb-8">
                あなた自身、あるいは右腕となる社員と共に、会社が自走する最強の仕組みを創り上げましょう。
              </p>
              <p className="text-2xl sm:text-3xl font-black text-[#FF6B35] mb-10 leading-[1.5]">
                決断して、共に走り出そう。
              </p>
              <div className="text-center">
                <a
                  href="#contact"
                  className="inline-flex items-center gap-3 px-10 sm:px-12 py-4 sm:py-5 bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white text-base sm:text-lg font-bold rounded-full hover:shadow-2xl hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
                >
                  無料個別相談を予約する
                  <i className="ri-arrow-right-line text-2xl"></i>
                </a>
                <p className="text-sm text-gray-500 mt-4">所要時間:30分 / オンライン対応</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-4xl sm:text-5xl font-black text-[#1A2332] mb-4 leading-[1.5]">ご質問・ご相談</h2>
            <p className="text-base sm:text-xl text-gray-600 leading-[1.8]">「自社の場合はどうなるか？」など、お気軽にご相談ください。</p>
          </div>

          <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-8 sm:p-12 shadow-xl">
            <form id="contact-form" data-readdy-form onSubmit={handleContactSubmit}>
              <div className="space-y-5 sm:space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-[#1A2332] mb-2">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-5 sm:px-6 py-3 sm:py-4 bg-white border-2 border-gray-200 rounded-xl focus:border-[#FF6B35] focus:outline-none transition-colors text-sm sm:text-base"
                    placeholder="山﨑 篤史"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-[#1A2332] mb-2">
                    メールアドレス <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-5 sm:px-6 py-3 sm:py-4 bg-white border-2 border-gray-200 rounded-xl focus:border-[#FF6B35] focus:outline-none transition-colors text-sm sm:text-base"
                    placeholder="example@company.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-[#1A2332] mb-2">
                    電話番号 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-5 sm:px-6 py-3 sm:py-4 bg-white border-2 border-gray-200 rounded-xl focus:border-[#FF6B35] focus:outline-none transition-colors text-sm sm:text-base"
                    placeholder="090-0000-0000"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-semibold text-[#1A2332] mb-2">
                    会社名/屋号/その他
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full px-5 sm:px-6 py-3 sm:py-4 bg-white border-2 border-gray-200 rounded-xl focus:border-[#FF6B35] focus:outline-none transition-colors text-sm sm:text-base"
                    placeholder="株式会社〇〇 / 個人事業 / その他"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-[#1A2332] mb-2">
                    お問い合わせ内容 <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={6}
                    maxLength={500}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-5 sm:px-6 py-3 sm:py-4 bg-white border-2 border-gray-200 rounded-xl focus:border-[#FF6B35] focus:outline-none transition-colors resize-none text-sm sm:text-base"
                    placeholder="ご相談内容をお聞かせください（500文字以内）"
                  ></textarea>
                  <div className="text-right text-sm text-gray-500 mt-1">
                    {formData.message.length} / 500
                  </div>
                </div>

                {/* 個人情報同意 */}
                <div className="bg-gray-50 rounded-xl p-5 border-2 border-gray-200">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <div className="relative flex-shrink-0 mt-0.5">
                      <input
                        type="radio"
                        name="consent"
                        id="consent"
                        required
                        checked={formData.consent}
                        onChange={() => setFormData({ ...formData, consent: true })}
                        className="sr-only"
                      />
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${formData.consent ? 'border-[#FF6B35] bg-[#FF6B35]' : 'border-gray-400 bg-white'}`}
                      >
                        {formData.consent && (
                          <div className="w-2 h-2 rounded-full bg-white"></div>
                        )}
                      </div>
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-[#1A2332]">
                        個人情報の取り扱いについて同意します <span className="text-red-500">*</span>
                      </span>
                      <p className="text-xs text-gray-500 mt-1">
                        <a
                          href="https://buildupmarketing.jp/privacypolicy/"
                          target="_blank"
                          rel="nofollow noopener noreferrer"
                          className="text-[#FF6B35] underline hover:opacity-80 transition-opacity"
                        >
                          プライバシーポリシーを確認する
                        </a>
                      </p>
                    </div>
                  </label>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || formData.message.length > 500 || !formData.consent}
                  className="w-full px-8 py-4 sm:py-5 bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white text-base sm:text-lg font-bold rounded-full hover:shadow-2xl hover:scale-105 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? '送信中...' : '送信する'}
                </button>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border-2 border-green-200 rounded-xl text-center">
                    <p className="text-green-700 font-semibold text-sm sm:text-base">お問い合わせありがとうございます。担当者より折り返しご連絡いたします。</p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border-2 border-red-200 rounded-xl text-center">
                    <p className="text-red-700 font-semibold text-sm sm:text-base">送信に失敗しました。もう一度お試しください。</p>
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#F5F1E8] py-8 mx-4 sm:mx-6 mb-4 sm:mb-6 rounded-[1.5rem] sm:rounded-[2rem]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-gray-500">
              © 2026 脳内カオス解体プロジェクト
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
              <a
                href="https://buildupmarketing.jp/tokuteisho/"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="text-sm text-gray-600 hover:text-[#FF6B35] transition-colors cursor-pointer"
              >
                特定商取引法に基づく表記
              </a>
              <a
                href="https://buildupmarketing.jp/privacypolicy/"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="text-sm text-gray-600 hover:text-[#FF6B35] transition-colors cursor-pointer"
              >
                プライバシーポリシー
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
